import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Send, MessageCircle, FileText, Users, Clock, ArrowLeft } from 'lucide-react'

export default function TeamCollaborationPage() {
  const navigate = useNavigate()
  const [messages, setMessages] = useState([
    { id: 1, author: 'Sarah Chen', avatar: '👩', message: 'Just finished the design mockups for the dashboard', timestamp: '2 hours ago', reactions: ['👍', '❤️'] },
    { id: 2, author: 'Mike Johnson', avatar: '👨', message: 'Great work! I\'ll start the backend integration next', timestamp: '1 hour ago', reactions: ['👍'] },
    { id: 3, author: 'Emma Wilson', avatar: '👩‍🦰', message: 'Do we need to update the API documentation?', timestamp: '30 mins ago', reactions: [] },
  ])

  const [files, setFiles] = useState([
    { id: 1, name: 'Design Mockups.pdf', uploadedBy: 'Sarah Chen', date: '2026-05-04', size: '4.2 MB' },
    { id: 2, name: 'API Specification.doc', uploadedBy: 'Mike Johnson', date: '2026-05-03', size: '1.8 MB' },
    { id: 3, name: 'Project Charter.xlsx', uploadedBy: 'Alex Kumar', date: '2026-05-02', size: '2.5 MB' },
  ])

  const [newMessage, setNewMessage] = useState('')
  const [activeTab, setActiveTab] = useState('chat')

  const stats = useMemo(() => ({
    totalMessages: messages.length,
    totalFiles: files.length,
    teamMembers: 8,
  }), [messages, files])

  const handleSendMessage = () => {
    if (!newMessage.trim()) return
    setMessages([...messages, {
      id: Date.now(),
      author: 'You',
      avatar: '🧑',
      message: newMessage,
      timestamp: 'now',
      reactions: []
    }])
    setNewMessage('')
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="max-w-7xl mx-auto">
        <button onClick={() => navigate('/projects')} className="flex items-center gap-2 text-blue-400 hover:text-blue-300 mb-6 font-semibold">
          <ArrowLeft size={20} /> Back to Project Management
        </button>
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Team Collaboration</h1>
          <p className="text-slate-400">Chat, file sharing, and team communication</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Team Members</div>
            <div className="text-3xl font-bold text-blue-400">{stats.teamMembers}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Messages</div>
            <div className="text-3xl font-bold text-green-400">{stats.totalMessages}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Shared Files</div>
            <div className="text-3xl font-bold text-yellow-400">{stats.totalFiles}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Active Now</div>
            <div className="text-3xl font-bold text-purple-400">5</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 overflow-hidden flex flex-col h-96">
            <div className="border-b border-blue-500/20 p-4">
              <div className="flex gap-4">
                <button
                  onClick={() => setActiveTab('chat')}
                  className={`flex items-center gap-2 px-4 py-2 rounded ${activeTab === 'chat' ? 'bg-blue-600 text-white' : 'text-slate-400'}`}
                >
                  <MessageCircle size={18} /> Chat
                </button>
                <button
                  onClick={() => setActiveTab('files')}
                  className={`flex items-center gap-2 px-4 py-2 rounded ${activeTab === 'files' ? 'bg-blue-600 text-white' : 'text-slate-400'}`}
                >
                  <FileText size={18} /> Files
                </button>
              </div>
            </div>

            {activeTab === 'chat' && (
              <>
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.map((msg) => (
                    <div key={msg.id} className="bg-slate-700/30 rounded-lg p-4">
                      <div className="flex items-start gap-3 mb-2">
                        <span className="text-2xl">{msg.avatar}</span>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-white text-sm">{msg.author}</p>
                            <p className="text-xs text-slate-400">{msg.timestamp}</p>
                          </div>
                          <p className="text-sm text-slate-300 mt-1">{msg.message}</p>
                        </div>
                      </div>
                      {msg.reactions.length > 0 && (
                        <div className="flex gap-2 mt-2 ml-11 text-sm">
                          {msg.reactions.map((r, i) => <span key={i}>{r}</span>)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="border-t border-blue-500/20 p-4 flex gap-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder="Type a message..."
                    className="flex-1 bg-slate-700 border border-blue-500/20 rounded px-3 py-2 text-white placeholder-slate-500"
                  />
                  <button onClick={handleSendMessage} className="p-2 bg-blue-600 hover:bg-blue-700 rounded text-white">
                    <Send size={18} />
                  </button>
                </div>
              </>
            )}

            {activeTab === 'files' && (
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {files.map((file) => (
                  <div key={file.id} className="bg-slate-700/30 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText size={24} className="text-blue-400" />
                      <div>
                        <p className="text-sm font-semibold text-white">{file.name}</p>
                        <p className="text-xs text-slate-400">{file.uploadedBy} • {file.date}</p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-400">{file.size}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <h3 className="text-lg font-bold text-white mb-4">Team Members</h3>
            <div className="space-y-3">
              {[
                { name: 'Sarah Chen', role: 'Designer', status: 'online' },
                { name: 'Mike Johnson', role: 'Developer', status: 'online' },
                { name: 'Emma Wilson', role: 'QA Lead', status: 'away' },
                { name: 'Alex Kumar', role: 'Backend Dev', status: 'online' },
              ].map((member, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${member.status === 'online' ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                    <div>
                      <p className="text-sm font-semibold text-white">{member.name}</p>
                      <p className="text-xs text-slate-400">{member.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
